import { useQuery, useQueryClient } from '@tanstack/react-query';
import { type FormEvent, useEffect, useRef, useState } from 'react';
import { NavLink, Outlet, useNavigate } from 'react-router';
import type { SessionInfo } from '../../shared/session';
import { api } from '../lib/api';
import { fmtNum, fmtRelative } from '../lib/format';
import { Icon, type IconName } from './ui';

const readTheme = (): 'light' | 'dark' => {
  try {
    const s = localStorage.getItem('bc.theme');
    if (s === 'dark' || s === 'light') return s;
  } catch {}
  return window.matchMedia?.('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
};

interface NavItem {
  to: string;
  label: string;
  icon: IconName;
  count?: number;
  alert?: boolean;
  end?: boolean;
}

export function Layout({ session }: { session: SessionInfo }) {
  const [theme, setTheme] = useState(readTheme);
  const qc = useQueryClient();
  const navigate = useNavigate();
  const searchRef = useRef<HTMLInputElement>(null);
  const [jump, setJump] = useState('');
  useEffect(() => {
    document.documentElement.dataset.theme = theme;
    try {
      localStorage.setItem('bc.theme', theme);
    } catch {}
  }, [theme]);
  // "/" focuses brand search from anywhere (except while typing in a field).
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const el = e.target as HTMLElement;
      if (e.key === '/' && !/INPUT|TEXTAREA|SELECT/.test(el.tagName)) {
        e.preventDefault();
        searchRef.current?.focus();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);
  const overview = useQuery({ queryKey: ['overview'], queryFn: api.overview, staleTime: 30_000 });
  const o = overview.data;

  const groups: Array<{ title: string; items: NavItem[] }> = [
    {
      title: 'Catalog',
      items: [
        { to: '/', label: 'Brands', icon: 'catalog', count: o?.entities.total, end: true },
        { to: '/candidates', label: 'Candidates', icon: 'candidates', count: o?.candidates.total },
      ],
    },
    {
      title: 'Quality',
      items: [
        {
          to: '/reviews',
          label: 'Review queue',
          icon: 'review',
          count: o?.reviews.open,
          alert: !!o?.reviews.blockingOpen,
        },
      ],
    },
    {
      title: 'Pipeline',
      items: [
        {
          to: '/operations',
          label: 'ETL operations',
          icon: 'ops',
          count: o?.runs.failed24h || undefined,
          alert: !!o?.runs.failed24h,
        },
      ],
    },
  ];

  const submitJump = (e: FormEvent) => {
    e.preventDefault();
    const q = jump.trim();
    navigate(q ? `/?q=${encodeURIComponent(q)}` : '/');
    setJump('');
    searchRef.current?.blur();
  };

  return (
    <div className="shell">
      <a href="#main" className="skip-link">
        Skip to content
      </a>
      <aside className="sidebar" aria-label="Primary">
        <NavLink to="/" className="brandmark" aria-label="Brand Intelligence Console — home">
          <div className="mark" aria-hidden="true">
            BI
          </div>
          <div>
            <div className="t1">Brand Intelligence</div>
            <div className="t2">Console</div>
          </div>
        </NavLink>
        <nav className="nav" aria-label="Sections">
          {groups.map((g) => (
            <div key={g.title} className="nav-group">
              <div className="nav-title">{g.title}</div>
              {g.items.map((n) => (
                <NavLink key={n.to} to={n.to} end={n.end}>
                  <Icon name={n.icon} />
                  <span>{n.label}</span>
                  {n.count !== undefined && (
                    <span className={`count${n.alert ? ' alert' : ''}`}>{fmtNum(n.count)}</span>
                  )}
                </NavLink>
              ))}
            </div>
          ))}
        </nav>
        <div className="foot">
          <div className="env-card" title="The V3 environment this console reads from">
            <span className="env-badge">{session.environment}</span>
            <span className="muted">Read-only view of the V3 catalog</span>
          </div>
        </div>
      </aside>
      <div className="main">
        <header className="topbar">
          <search className="jump-wrap">
            <form className="jump" aria-label="Find a brand" onSubmit={submitJump}>
              <Icon name="search" />
              <input
                ref={searchRef}
                className="input"
                type="search"
                aria-label="Find a brand"
                placeholder="Find a brand by name, slug or domain"
                value={jump}
                onChange={(e) => setJump(e.target.value)}
              />
              <kbd>/</kbd>
            </form>
          </search>
          <div className="spacer" />
          <button
            type="button"
            className="btn sm ghost"
            aria-label={theme === 'dark' ? 'Switch to light theme' : 'Switch to dark theme'}
            aria-pressed={theme === 'dark'}
            onClick={() => setTheme(theme === 'dark' ? 'light' : 'dark')}
          >
            <Icon name={theme === 'dark' ? 'sun' : 'moon'} />
          </button>
          <div className="who hide-sm" title={session.expiresAt ?? ''}>
            <span className="email">{session.email}</span>
            <span className="muted">
              {session.authMode === 'workos' ? 'WorkOS' : 'Local dev sign-in'}
              {session.expiresAt
                ? ` · session renews on activity, ends ${fmtRelative(session.expiresAt)}`
                : ''}
            </span>
          </div>
          <button
            type="button"
            className="btn sm"
            onClick={async () => {
              await api.logout().catch(() => {});
              qc.clear();
              navigate('/login', { replace: true });
            }}
          >
            <Icon name="logout" />
            <span className="hide-sm">Sign out</span>
          </button>
        </header>
        <main className="content" id="main" tabIndex={-1}>
          <Outlet />
        </main>
      </div>
    </div>
  );
}
